import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { dmYhm } from "@/lib/dateFmt";
import { Megaphone, PaperPlaneRight, CheckCircle, Clock, Users, Envelope, Translate, Bell } from "@phosphor-icons/react";

// v1.28.56 — Broadcast Message · Phase 2 (In-App + Email + auto Hindi)
export default function BroadcastMessage() {
    const [message, setMessage] = useState("");
    const [hindi, setHindi] = useState("");
    const [translating, setTranslating] = useState(false);
    const [audience, setAudience] = useState("all");
    const [parties, setParties] = useState([]);
    const [selectedParties, setSelectedParties] = useState([]);
    const [history, setHistory] = useState([]);
    const [sending, setSending] = useState(false);
    const [detail, setDetail] = useState(null);
    const [reminding, setReminding] = useState(false);
    const [cooldownMin, setCooldownMin] = useState(0);

    const load = async () => {
        try { setHistory((await api.get("/broadcasts")).data || []); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => {
        load();
        api.get("/parties").then((r) => setParties(r.data || [])).catch(() => setParties([]));
    }, []);

    // Debounced auto-translate as admin types.
    const translate = useCallback(async (text) => {
        if (!text.trim()) { setHindi(""); return; }
        setTranslating(true);
        try {
            const r = await api.post("/broadcasts/translate", { text });
            setHindi(r.data?.hindi || "");
        } catch { /* silent */ }
        finally { setTranslating(false); }
    }, []);

    useEffect(() => {
        const id = setTimeout(() => translate(message), 800);
        return () => clearTimeout(id);
    }, [message, translate]);

    const send = async () => {
        if (!message.trim()) { toast.error("Message is required"); return; }
        setSending(true);
        try {
            const body = { message: message.trim(), hindi_message: hindi.trim(), audience };
            if (audience === "selected_parties") body.target_ids = selectedParties;
            const r = await api.post("/broadcasts", body);
            toast.success(`Sent to ${r.data.recipient_count} recipient${r.data.recipient_count === 1 ? "" : "s"} · In-App + Email`);
            setMessage(""); setHindi(""); setSelectedParties([]); setAudience("all");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSending(false); }
    };

    const openDetail = async (id) => {
        try { setDetail((await api.get(`/broadcasts/${id}`)).data); setCooldownMin(0); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const remindPending = async () => {
        if (!detail) return;
        const pendingCount = detail.pending?.length || 0;
        if (!pendingCount) { toast.info("Everyone has already acknowledged."); return; }
        const cd = Math.max(0, parseInt(cooldownMin, 10) || 0);
        const label = cd > 0 ? ` (skip users reminded in last ${cd} min)` : "";
        if (!window.confirm(`Re-send email to ${pendingCount} pending user${pendingCount === 1 ? "" : "s"}${label}?`)) return;
        setReminding(true);
        try {
            const r = await api.post(`/broadcasts/${detail.id}/remind`, { cooldown_minutes: cd });
            const d = r.data || {};
            const parts = [`${d.reminded || 0} reminded`];
            if (d.failed) parts.push(`${d.failed} failed`);
            if (d.skipped_cooldown) parts.push(`${d.skipped_cooldown} skipped (cooldown)`);
            toast.success(parts.join(" · "));
            // Refresh detail so email_status badges reflect new sends.
            const fresh = await api.get(`/broadcasts/${detail.id}`);
            setDetail(fresh.data);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setReminding(false); }
    };

    return (
        <div className="mx-auto max-w-6xl px-4 py-6" data-testid="broadcast-page">
            <div className="mb-4 flex items-center gap-2">
                <Megaphone size={20} className="text-amber-600" weight="fill" />
                <h1 className="text-2xl font-bold text-slate-900">Broadcast Message</h1>
            </div>

            <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Message ({message.length}/500) — English
                </label>
                <textarea
                    data-testid="broadcast-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                    rows={3}
                    placeholder="Enter announcement in English…"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                />

                <label className="mt-3 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-600">
                    <Translate size={12} /> Hindi Preview (editable) {translating && <span className="normal-case text-[10px] text-amber-600">translating…</span>}
                </label>
                <textarea
                    data-testid="broadcast-hindi-input"
                    value={hindi}
                    onChange={(e) => setHindi(e.target.value)}
                    rows={2}
                    placeholder="हिंदी अनुवाद यहाँ दिखेगा…"
                    style={{ fontFamily: "'Noto Sans Devanagari', system-ui, sans-serif" }}
                    className="mt-1 w-full rounded-md border border-slate-300 bg-amber-50/40 px-3 py-2 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                />

                <label className="mt-3 block text-xs font-semibold uppercase tracking-wider text-slate-600">Recipients</label>
                <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-4">
                    {[
                        { v: "all", l: "All Users" },
                        { v: "parties", l: "All Parties" },
                        { v: "staff", l: "All Staff" },
                        { v: "selected_parties", l: "Selected Parties" },
                    ].map((o) => (
                        <label key={o.v} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${audience === o.v ? "border-amber-400 bg-amber-50 font-semibold text-amber-900" : "border-slate-200 hover:bg-slate-50"}`}>
                            <input type="radio" name="audience" value={o.v} checked={audience === o.v} onChange={() => setAudience(o.v)} data-testid={`broadcast-audience-${o.v}`} />
                            {o.l}
                        </label>
                    ))}
                </div>

                {audience === "selected_parties" && (
                    <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-slate-200 p-2">
                        {parties.map((p) => (
                            <label key={p.id} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-slate-50">
                                <input
                                    type="checkbox"
                                    checked={selectedParties.includes(p.id)}
                                    onChange={(e) => setSelectedParties((cur) => e.target.checked ? [...cur, p.id] : cur.filter((x) => x !== p.id))}
                                    data-testid={`broadcast-party-${p.id}`}
                                />
                                {p.name}
                            </label>
                        ))}
                        {parties.length === 0 && <div className="p-2 text-xs text-slate-400">No parties available</div>}
                    </div>
                )}

                <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
                    <b>Channels fired:</b> In-App blocking popup <Envelope size={11} className="inline" /> Email · both English and Hindi.
                </div>

                <div className="mt-4 flex justify-end">
                    <button type="button" onClick={send} disabled={sending || !message.trim()} data-testid="broadcast-send-btn"
                        className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-60">
                        <PaperPlaneRight size={14} weight="fill" />
                        {sending ? "Sending…" : "Send Now"}
                    </button>
                </div>
            </div>

            <h2 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-600">Broadcast History</h2>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        <tr>
                            <th className="px-3 py-2 text-left">When</th>
                            <th className="px-3 py-2 text-left">Message</th>
                            <th className="px-3 py-2 text-left">Sender</th>
                            <th className="px-3 py-2 text-right">Recipients</th>
                            <th className="px-3 py-2 text-right">Acked</th>
                            <th className="px-3 py-2 text-right">Pending</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No broadcasts yet.</td></tr>}
                        {history.map((b) => (
                            <tr key={b.id} data-testid={`broadcast-row-${b.id}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(b.id)}>
                                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{dmYhm(b.created_at)}</td>
                                <td className="px-3 py-2 text-slate-800 max-w-md truncate">{b.message}</td>
                                <td className="px-3 py-2 text-slate-600">{b.created_by_name || b.created_by}</td>
                                <td className="px-3 py-2 text-right"><Users size={12} className="inline text-slate-400" /> {b.recipient_count}</td>
                                <td className="px-3 py-2 text-right text-emerald-700"><CheckCircle size={12} weight="fill" className="inline" /> {b.acknowledged_count}</td>
                                <td className="px-3 py-2 text-right text-amber-700"><Clock size={12} weight="fill" className="inline" /> {b.pending_count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {detail && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetail(null)}>
                    <div className="max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="broadcast-detail-modal">
                        <div className="mb-2 flex items-start justify-between gap-3">
                            <div className="flex-1">
                                <div className="text-xs uppercase tracking-wider text-slate-500">Sent {dmYhm(detail.created_at)} by {detail.created_by_name || detail.created_by}</div>
                                <div className="mt-1 text-base font-semibold text-slate-900 whitespace-pre-wrap">{detail.message}</div>
                                {detail.hindi_message && <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap" style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}>{detail.hindi_message}</div>}
                            </div>
                            <button onClick={() => setDetail(null)} className="rounded p-1 text-slate-400 hover:bg-slate-100">✕</button>
                        </div>

                        {detail.pending && detail.pending.length > 0 && (
                            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2">
                                <Bell size={14} weight="fill" className="text-amber-700" />
                                <span className="text-xs font-semibold text-amber-900">
                                    {detail.pending.length} user{detail.pending.length === 1 ? "" : "s"} pending
                                </span>
                                <div className="ml-auto flex items-center gap-2">
                                    <label className="flex items-center gap-1 text-[11px] text-slate-600">
                                        Cooldown
                                        <input
                                            type="number"
                                            min={0}
                                            max={1440}
                                            value={cooldownMin}
                                            onChange={(e) => setCooldownMin(e.target.value)}
                                            data-testid="broadcast-remind-cooldown"
                                            className="w-16 rounded border border-slate-300 px-1.5 py-0.5 text-xs outline-none focus:border-amber-400"
                                        />
                                        min
                                    </label>
                                    <button
                                        type="button"
                                        onClick={remindPending}
                                        disabled={reminding}
                                        data-testid="broadcast-remind-pending-btn"
                                        className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-60"
                                    >
                                        <Bell size={12} weight="fill" />
                                        {reminding ? "Reminding…" : "Remind Pending"}
                                    </button>
                                </div>
                            </div>
                        )}
                        {detail.last_reminded_at && (
                            <div className="mt-1 text-[11px] text-slate-500">
                                Last reminded {dmYhm(detail.last_reminded_at)}
                                {detail.reminder_count ? ` · ${detail.reminder_count} time${detail.reminder_count === 1 ? "" : "s"}` : ""}
                            </div>
                        )}

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Acknowledged ({detail.acknowledged.length})</div>
                                <ul className="mt-1 space-y-0.5 text-xs">
                                    {detail.acknowledged.map((u) => (
                                        <li key={u.user_id} className="flex justify-between gap-2">
                                            <span className="truncate">{u.name || u.email}</span>
                                            <span className="text-slate-500 whitespace-nowrap">{dmYhm(u.acked_at)} · <EmailBadge s={u.email_status} /></span>
                                        </li>
                                    ))}
                                    {detail.acknowledged.length === 0 && <li className="text-slate-400 italic">None yet</li>}
                                </ul>
                            </div>
                            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Pending ({detail.pending.length})</div>
                                <ul className="mt-1 space-y-0.5 text-xs">
                                    {detail.pending.map((u) => (
                                        <li key={u.user_id} className="flex justify-between gap-2"><span className="truncate">{u.name || u.email}</span><EmailBadge s={u.email_status} /></li>
                                    ))}
                                    {detail.pending.length === 0 && <li className="text-slate-400 italic">Everyone acknowledged</li>}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function EmailBadge({ s }) {
    const map = {
        sent:     { c: "bg-emerald-100 text-emerald-800", l: "Email Sent" },
        failed:   { c: "bg-rose-100 text-rose-800",       l: "Email Failed" },
        no_email: { c: "bg-slate-200 text-slate-700",     l: "No Email" },
    };
    const v = map[s] || map.no_email;
    return <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold ${v.c}`}><Envelope size={9} /> {v.l}</span>;
}
